// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import { FormTextField } from '@/components/form-text-field';
import { Warning as WarningIcon } from '@/constants/hardfin-standard-icons';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { useSupportError } from '@/hooks/use-support-error';
import { cancelShipment } from '@/shipment/api/cancel-shipment';
import { Shipment } from '@/shipment/api/types';
import { shipmentsQueryKey } from '@/shipment/hooks/use-shipments';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  AlertTitle,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

const ERROR_CODE_SHIPMENT_CANNOT_CANCEL = 2251;

const formSchema = z.object({
  reason: z.string().min(1, 'A reason is required to cancel this shipment'),
  removeLinkedShippingCost: z.boolean(),
});

type FormData = z.infer<typeof formSchema>;

interface Props {
  isOpen: boolean;
  shipmentKey: string;
  shipmentNumber: string;
  shippingCost: Shipment['shippingCost'];
  onClose: () => void;
  // Optional: the dialog already refreshes the shipment list. Callers that hold
  // their own copy of the shipment pass this to take the cancelled one.
  onSuccess?: (updatedShipment: Shipment) => void;
}

/**
 * Dialog component for cancelling a shipment.
 * Requires a reason for cancellation before submitting.
 */
export function ShipmentCancelDialog(props: Props) {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const { showSupportError, hideSupportError } = useSupportError();
  const queryClient = useQueryClient();
  const linkedShippingCostLabel =
    props.shippingCost === null
      ? null
      : formatCurrencyValue(props.shippingCost);

  const cancelMutation = useMutation({
    mutationFn: (data: FormData) =>
      cancelShipment(props.shipmentKey, {
        cancellationReason: data.reason,
        removeLinkedShippingCost: data.removeLinkedShippingCost,
      }),
    onSuccess: (updatedShipment) => {
      queryClient.invalidateQueries({ queryKey: shipmentsQueryKey });
      close({ ignorePending: true });
      props.onSuccess?.(updatedShipment);
    },
    onError: (error) => {
      const apiError = handleAPIError(error);
      // Reset both surfaces so a retry never leaves the prior global
      // support alert visible alongside the new inline error.
      setErrorMessage('');
      hideSupportError();
      const curated = getUserFacingErrorMessage(apiError);
      if (curated !== SUPPORT_FALLBACK_ERROR_MESSAGE) {
        setErrorMessage(curated);
      } else if (apiError.apiStatusCode === ERROR_CODE_SHIPMENT_CANNOT_CANCEL) {
        setErrorMessage(
          'This shipment cannot be cancelled in its current state.'
        );
      } else {
        if (!apiError.sentToSentry) {
          captureException(error);
        }
        showSupportError('An error occurred while cancelling the shipment.');
      }
    },
  });

  const { control, reset, handleSubmit, formState } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      reason: '',
      removeLinkedShippingCost: false,
    },
  });

  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    if (!props.shipmentKey) {
      return;
    }
    return cancelMutation.mutate(data);
  };

  const close = (options?: { ignorePending?: boolean }) => {
    if (cancelMutation.isPending && !options?.ignorePending) {
      return;
    }
    props.onClose();
    reset();
    setErrorMessage('');
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={props.isOpen}
      onClose={cancelMutation.isPending ? undefined : close}
      disableEscapeKeyDown={cancelMutation.isPending}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit(handleValidSubmission),
        sx: {
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 2,
        }}
      >
        Cancel shipment
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        <Alert
          severity="warning"
          icon={<WarningIcon fontSize="medium" />}
          sx={{
            marginY: 3,
            alignItems: 'center',
          }}
        >
          <AlertTitle variant="h6">
            Are you sure you want to cancel shipment{' '}
            <strong>{props.shipmentNumber}</strong>?
          </AlertTitle>
          <Typography variant="body1">
            Cancelling this shipment is permanent and cannot be undone.
          </Typography>
        </Alert>

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errorMessage}
          </Alert>
        )}

        <Typography
          variant="subtitle1"
          gutterBottom
          sx={{ marginTop: 2, marginBottom: 1 }}
        >
          Cancellation reason
        </Typography>

        <FormTextField
          control={control}
          name="reason"
          fullWidth={true}
          isMultiline={true}
          minimumRows={3}
          placeholder="Please provide a reason for cancellation"
        />

        {linkedShippingCostLabel !== null && (
          <Controller
            control={control}
            name="removeLinkedShippingCost"
            render={({ field }) => (
              <FormControlLabel
                sx={{ marginTop: 2 }}
                slotProps={{ typography: { variant: 'body2' } }}
                control={
                  <Checkbox
                    checked={field.value}
                    onChange={(_, checked) => field.onChange(checked)}
                  />
                }
                label={`Remove linked shipping cost (${linkedShippingCostLabel})`}
              />
            )}
          />
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Button
          onClick={() => close()}
          variant="outlined"
          disabled={cancelMutation.isPending}
        >
          No, keep shipment
        </Button>
        <Button
          type="submit"
          color="error"
          variant="contained"
          disabled={formState.isSubmitting || cancelMutation.isPending}
          sx={{ ml: 1.5 }}
        >
          Yes, cancel shipment
        </Button>
      </DialogActions>
    </Dialog>
  );
}
